import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Play, Quote } from "lucide-react";
import { Link } from "react-router-dom";

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" } as any,
  transition: { duration: 0.6 },
};

const talkingPoints = [
  "Why temporary fixes fail — and why they keep being repeated",
  "Why imported models often miss the mark in Hawaiʻi",
  "Why leadership requires direct language, not managed messaging",
  "Why Hawaiʻi needs systems built for Hawaiʻi, not adapted from elsewhere",
  "Why this podcast exists — and who it is for",
];

const quotes = [
  "There are real issues shaping Hawaiʻi right now, and too often we talk around them instead of through them.",
  "We do not need more noise. We need more clarity. More honesty. More responsibility in how we think, how we lead, and how we build.",
  "If we are serious about Hawaiʻi's future, then we need more conversations that are willing to be serious.",
];

export default function MaiKaWahaEpisodePage() {
  return (
    <PublicLayout>
      <SEOHead
        title="What Hawaiʻi Actually Needs: Beyond Temporary Fixes — Mai Ka Waha"
        description="Episode 1 of Mai Ka Waha. A direct conversation about what is working, what is not, and what it will take to build something better."
        url="https://davidisei.com/media/mai-ka-waha/ep1"
        type="article"
      />

      {/* Header */}
      <section className="pt-20 pb-12 max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div {...fade}>
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-medium mb-4">
            Mai Ka Waha — Episode 1
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground leading-[1.1] mb-5">
            What Hawaiʻi Actually Needs: Beyond Temporary Fixes
          </h1>
          <p className="text-[17px] text-muted-foreground leading-[1.75] max-w-xl">
            Introducing the voice, the tone, and the purpose behind this podcast. A direct conversation about what is working, what is not, and what it will take to build something better.
          </p>
        </motion.div>
      </section>

      {/* Video Placeholder */}
      <section className="pb-12">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <div className="aspect-video rounded-md bg-muted/40 border border-border/40 flex items-center justify-center">
              <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-background border border-border/40 flex items-center justify-center mx-auto mb-3">
                  <Play className="h-6 w-6 text-muted-foreground ml-0.5" />
                </div>
                <p className="text-[13px] text-muted-foreground">Episode coming soon</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Opening Statement */}
      <section className="py-14 border-t border-border/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium mb-6">
              Opening Statement
            </h2>
            <div className="space-y-4 text-[17px] text-foreground leading-[1.8]">
              <p>Welcome to Mai Ka Waha.</p>
              <p>This podcast is about direct conversation. Not performance. Not slogans. Not surface-level commentary.</p>
              <p>There are real issues shaping Hawaiʻi right now, and too often we talk around them instead of through them. Healthcare. Leadership. Workforce. Public systems. Community trust. Sustainability. These are not abstract topics. They affect how people live.</p>
              <p>The goal here is simple. To have honest discussions about what is working, what is not, and what it will take to build something better.</p>
              <p>This is not a space for empty noise. It is a space for clarity.</p>
              <p>And if we are serious about Hawaiʻi's future, then we need more conversations that are willing to be serious.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Talking Points */}
      <section className="py-14 bg-muted/25">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium mb-6">
              Key Talking Points
            </h2>
            <div className="space-y-0">
              {talkingPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-3 py-4 border-b border-border/30 last:border-0">
                  <span className="text-[13px] font-mono text-muted-foreground/40 mt-[2px] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-[15px] text-foreground leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Quotes */}
      <section className="py-14 border-t border-border/30">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium mb-8">
              Featured Quotes
            </h2>
            <div className="space-y-8">
              {quotes.map((q, i) => (
                <div key={i} className="border-l-2 border-foreground/10 pl-6">
                  <p className="text-[17px] text-foreground leading-[1.8] italic">"{q}"</p>
                  <p className="text-[12px] text-muted-foreground/50 mt-2">— David Isei</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Written Perspective */}
      <section className="py-14 bg-muted/25">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium mb-6">
              Related Written Perspective
            </h2>
            <div className="border border-border/40 rounded-md p-6">
              <h3 className="text-[15px] font-semibold text-foreground mb-2">
                Why Temporary Fixes Keep Failing Hawaiʻi
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                A written companion to Episode 1. Exploring why the cycle of short-term solutions persists and what it would take to break it.
              </p>
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground/50">Coming soon</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Clips */}
      <section className="py-14 border-t border-border/30">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium mb-6">
              Clips & Media Assets
            </h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {["Opening monologue", "On temporary fixes", "Closing statement"].map((label) => (
                <div key={label} className="aspect-[9/16] rounded-md bg-muted/40 border border-border/40 flex flex-col items-center justify-center p-4">
                  <Play className="h-5 w-5 text-muted-foreground/30 mb-2" />
                  <p className="text-[11px] text-muted-foreground/50 text-center">{label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-14 bg-muted/25">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <motion.div {...fade}>
            <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium mb-6">
              Closing Statement
            </h2>
            <div className="space-y-4 text-[17px] text-foreground leading-[1.8]">
              <p>If this conversation did one thing, I hope it made the standard clear.</p>
              <p>We do not need more noise. We need more clarity. More honesty. More responsibility in how we think, how we lead, and how we build.</p>
              <p>That is the spirit behind Mai Ka Waha.</p>
              <p>And that is the standard this platform will try to keep.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back to Media */}
      <section className="py-10 border-t border-border/30">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Link to="/media" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Media & Conversations
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
